import React, { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, InputField } from '@freelancelabs/components';

import { ContactsByEmailDocument } from '../../../graphql/__generated__/types';
import ContactMatchCard from './ContactMatchCard';
import { lookupRefusal } from './contactLookup';
import { Draft } from './draft';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
}

/**
 * Step 1: the address, and the contact it designates. Only contacts known to the source are
 * onboarded. An address can designate several contacts, so the operator always chooses; nothing
 * is auto-selected, even for a single result.
 */
const IdentityStep = ({ draft, onChange }: Props) => {
    const [searched, setSearched] = useState(draft.contact?.email ?? '');

    const typed = draft.email.trim();

    const { data, loading, error, refetch } = useQuery(ContactsByEmailDocument, {
        variables: { email: searched },
        skip: searched.length === 0,
    });

    // Whether the answer on screen is for the address in the field: Apollo keeps the previous
    // result while a new address is typed, and picking from a stale list adopts the wrong person.
    const current = searched.length > 0 && searched === typed;

    const matches = data?.contactsByEmail;
    const contacts = current ? (matches?.contacts ?? []) : [];
    const refusal = current ? lookupRefusal(matches?.emptyReason) : null;
    const contactCount = contacts.length;

    const search = (event: React.FormEvent) => {
        event.preventDefault();
        if (!typed) {
            return;
        }
        // Re-running the same address is legitimate: the source may have been ingested since.
        if (searched === typed) {
            void refetch();
            return;
        }
        setSearched(typed);
    };

    // Adopt the identity AND the address it was resolved for, in one patch: applyPatch compares them.
    const pick = (contact: (typeof contacts)[number]) =>
        onChange({
            contact: {
                id: contact.id,
                email: typed,
                firstName: contact.firstName,
                lastName: contact.lastName,
                jobFunction: contact.title,
                businessReference: contact.businessReference,
                onboardingStatus: contact.onboardingStatus,
            },
        });

    return (
        <div className="flex max-w-3xl flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding').t`Identify the contact`}</h2>
            </div>

            {/* A form, so Enter searches. The button carries input-field-inline-action to line up
                with the field, whose column reserves its help-text height even when empty. */}
            <form className="flex flex-wrap items-end gap-3" onSubmit={search}>
                <InputField
                    className="min-w-64"
                    rootClassName="flex-1"
                    label={c('Onboarding field').t`Email address`}
                    assistiveText={c('Onboarding field')
                        .t`It carries the invitation and becomes the sign-in identifier. Case and surrounding spaces do not matter.`}
                    type="email"
                    value={draft.email}
                    onValue={(value: string) => onChange({ email: value })}
                    autoFocus
                />
                <Button
                    className="input-field-inline-action"
                    type="submit"
                    category="default"
                    loading={loading}
                    disabled={!typed}
                >
                    {c('Onboarding action').t`Search`}
                </Button>
            </form>

            {error && (
                <div className="flex flex-wrap items-center gap-3 text-sm text-danger-600" role="alert">
                    <span>
                        {c('Onboarding lookup')
                            .t`The search failed. Nothing can be concluded about this address — this is not an answer saying the contact is unknown.`}
                    </span>
                    <Button category="weak" size="small" onClick={() => refetch()}>
                        {c('Action').t`Retry`}
                    </Button>
                </div>
            )}

            {!error && !loading && searched.length > 0 && !current && (
                <p className="text-xs text-text-600" role="status">
                    {c('Onboarding lookup')
                        .t`The address has changed since the last search. Search again to see who it designates.`}
                </p>
            )}

            {/* The three empty answers. The middle one says not to re-read the address: it is correct. */}
            {!error && !loading && refusal && (
                <Alert type={refusal.tone === 'danger' ? 'danger' : 'warning'} title={refusal.title}>
                    <p className="mt-1 text-sm">{refusal.body}</p>
                </Alert>
            )}

            {contactCount > 1 && (
                <Alert type="warning" title={c('Onboarding lookup').t`This address designates several contacts.`}>
                    <p className="mt-1 text-sm">
                        {ngettext(
                            msgid`${contactCount} ingested contact carries this address — usually a shared mailbox, sometimes a duplicate at the source. Pick the person being onboarded: the invitation is sent under their identity.`,
                            `${contactCount} ingested contacts carry this address — usually a shared mailbox, sometimes duplicates at the source. Pick the person being onboarded: the invitation is sent under their identity.`,
                            contactCount
                        )}
                    </p>
                </Alert>
            )}

            {contactCount > 0 && (
                <ul className="flex flex-col gap-3">
                    {contacts.map((contact) => (
                        <ContactMatchCard
                            key={contact.id}
                            contact={contact}
                            selected={draft.contact?.id === contact.id}
                            amongSeveral={contactCount > 1}
                            onPick={() => pick(contact)}
                            onUnpick={() => onChange({ contact: null })}
                        />
                    ))}
                </ul>
            )}

            {!error && !loading && searched.length === 0 && (
                <p className="rounded-lg border border-dashed border-border-norm px-4 py-6 text-center text-xs text-text-600">
                    {c('Onboarding lookup')
                        .t`Enter an address and search: only a contact known to the source can be onboarded, because the accounts, markets and perimeter all come from it.`}
                </p>
            )}
        </div>
    );
};

export default IdentityStep;
